import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { eventBus } from '@/events/eventBus';
import { playSound } from '@/sound/audioSystem';
import { latestWaiverForBooking } from '@/domain/waiverFlow';
import { onBookingConfirmed } from '@/domain/bookingConfirmationFlow';
import { flagNoRebookMidPiece } from '@/domain/noRebookFlow';
import { cashLedgerFor } from '@/domain/payoutFlow';
import { depositPaidAud } from '@/utils/bookingSummary';
import { seedNow, seedId } from '@/domain/seedContext';
import type {
  ArtistRebookIntent,
  Booking,
  Deposit,
  FutureReadinessSignal,
  MerchSaleLine,
  Piece,
  RebookAttribution,
  RebookOutcome,
  SalePayment,
  SessionPhotoKind,
  Waiver,
} from '@/domain/types';

function nowIso() {
  return seedNow() ?? useSimulationStore.getState().clockIso;
}

/**
 * Payments recorded at/after this sim hour belong to tomorrow's settlement lump
 * (the banking-lump cutoff). DISTINCT from CLOCKOFF_HOUR (18, paymentTiming.ts),
 * which is the 6pm collection clock-off - a different concept. Do not merge.
 */
export const SETTLEMENT_CUTOFF_HOUR = 17;

/** Hour-of-day in the ISO string's OWN offset (seeds share +10:00), cutoff-safe. */
function hourOfIso(iso: string): number {
  return Number(iso.slice(11, 13));
}

/**
 * Auto-stamped make-sale reference (Prompt E, client amendment). Replaces the
 * hand-written EFTPOS docket: it rides the transaction and is echoed back at
 * settlement so the bank lump auto-splits (E4). Deterministic from the booking +
 * day so re-runs are stable (no Date.now / Math.random).
 */
export function makeSaleReference(studioId: string, bookingId: string, atIso: string): string {
  const stu = studioId.replace(/[^a-z]/gi, '').slice(0, 3).toUpperCase().padEnd(3, 'X');
  const mmdd = atIso.slice(5, 7) + atIso.slice(8, 10);
  let hash = 0;
  for (let i = 0; i < bookingId.length; i += 1) hash = (hash * 31 + bookingId.charCodeAt(i)) >>> 0;
  const seq = (hash % 10000).toString().padStart(4, '0');
  return `MS-${stu}-${mmdd}-${seq}`;
}

/**
 * Stable chair number for an artist — one artist per chair. Deterministic from
 * the artist id so a seated booking always shows that artist's usual chair,
 * rather than a random booth. Used only as a fallback when the caller doesn't
 * pass an explicit booth (the floor UI seats by artist, not by picking a booth).
 */
function chairForArtist(artistId: string): number {
  let hash = 0;
  for (let i = 0; i < artistId.length; i += 1) hash = (hash * 31 + artistId.charCodeAt(i)) >>> 0;
  return (hash % 6) + 1;
}

/**
 * Result of a check-in attempt. Check-in is a hard-gated step: the client can
 * only be checked in once their medical waiver is signed. The failure is typed
 * (not a hidden button) so no UI path can bypass the gate — every surface that
 * offers "Check in" must handle the `waiver-unsigned` case.
 */
export type CheckInResult =
  | { ok: true; booking: Booking }
  | { ok: false; reason: 'not-found' }
  | { ok: false; reason: 'waiver-unsigned'; waiver?: Waiver };

/**
 * Step 1 of the floor flow. Marks the client as arrived at the desk
 * (`status: 'checked-in'` + `checkedInAt`). It deliberately does NOT seat them
 * or assign a booth — that is `seatBooking` (step 2).
 *
 * Waiver gate: the latest waiver for the booking must be `signed`, else the
 * check-in is refused with `reason: 'waiver-unsigned'`.
 *
 * Idempotent: calling it again once the client is already checked in / seated /
 * completed is a no-op that reports success.
 */
export function checkInBooking(bookingId: string): CheckInResult {
  const brain = useBrainStore.getState();
  const booking = brain.bookings[bookingId];
  if (!booking) return { ok: false, reason: 'not-found' };
  // Already at or past check-in — nothing to do, the gate is behind them.
  if (
    booking.status === 'checked-in' ||
    booking.status === 'in-chair' ||
    booking.status === 'completed'
  ) {
    return { ok: true, booking };
  }

  const waiver = latestWaiverForBooking(bookingId);
  if (waiver?.status !== 'signed') {
    return { ok: false, reason: 'waiver-unsigned', waiver };
  }

  const at = nowIso();
  const updated: Booking = { ...booking, status: 'checked-in', checkedInAt: at };
  brain.upsertBooking(updated);
  brain.appendActivity({
    id: `act-checkin-${booking.id}`,
    type: 'booking-checked-in',
    customerId: booking.customerId,
    studioId: booking.studioId,
    artistId: booking.artistId,
    body: `${booking.customerId} checked in for their ${booking.sessionLength.replace('-', ' ')} with ${booking.artistId}.`,
    timestamp: at,
  });
  playSound('inbound-ring');
  eventBus.publish('booking:checked-in', { bookingId });
  return { ok: true, booking: updated };
}

/** Result of a seat attempt. Seating is only valid from the `checked-in` state. */
export type SeatResult =
  | { ok: true; booking: Booking }
  | { ok: false; reason: 'not-found' }
  | { ok: false; reason: 'not-checked-in' };

/**
 * Step 2 of the floor flow. Seats a checked-in client into the chair
 * (`status: 'in-chair'` + `seatedAt`) and records the booth. Only valid from
 * `checked-in` — a booking that is still confirmed (never checked in) or already
 * in the chair is refused with `reason: 'not-checked-in'`.
 */
export function seatBooking(bookingId: string, boothNumber?: number): SeatResult {
  const brain = useBrainStore.getState();
  const booking = brain.bookings[bookingId];
  if (!booking) return { ok: false, reason: 'not-found' };
  if (booking.status !== 'checked-in') return { ok: false, reason: 'not-checked-in' };

  const at = nowIso();
  const updated: Booking = {
    ...booking,
    status: 'in-chair',
    seatedAt: at,
    boothNumber: boothNumber ?? booking.boothNumber ?? chairForArtist(booking.artistId),
  };
  brain.upsertBooking(updated);
  brain.appendActivity({
    id: `act-seat-${booking.id}`,
    type: 'booking-seated',
    customerId: booking.customerId,
    studioId: booking.studioId,
    artistId: booking.artistId,
    body: `${booking.customerId} seated in booth ${updated.boothNumber} with ${booking.artistId}.`,
    timestamp: at,
  });
  playSound('inbound-ring');
  eventBus.publish('booking:seated', { bookingId, boothNumber: updated.boothNumber });
  return { ok: true, booking: updated };
}

export interface CheckOutInput {
  bookingId: string;
  sessionNote?: string;
  futureReadinessPhrase?: string;
  futureReadinessTriggerDate?: string;
  hoursToAdd?: number;
  markPieceComplete?: boolean;
  /**
   * L3 (17 Jul): approximate sessions still left on the piece, captured on a
   * decline-path checkout (partway finished). Makes `estimatedTotalSessions` a
   * living number = sessions completed so far + this remainder. Ignored when the
   * piece is marked complete.
   */
  pieceSessionsLeft?: number;
  paymentMethod: 'card' | 'cash' | 'rolled-deposit' | 'mixed';
  amountPaidAud: number;
  rolloverDepositToNextBooking: boolean;
  /** Deposit applied to today's balance (partial deposit use). Defaults to 0. */
  depositAppliedAud?: number;
  /** Merch/aftercare items sold on this checkout (no commission). */
  merchLines?: MerchSaleLine[];
  /** Cash physically tendered (cash/mixed) — net (tendered − change) hits the ledger. */
  cashTenderedAud?: number;
  /** Reasoned +/- charge adjustment (D3), signed. Defaults to 0. */
  adjustmentAud?: number;
  adjustmentReason?: string;
  /**
   * Captured from the Rebook Outcome step. When present, drives the rebook
   * attribution + (for Outcome A) creates the locked-in next booking, (for
   * Outcome C) seeds the sales follow-up task.
   */
  rebookCapture?: {
    outcome: RebookOutcome;
    attribution: RebookAttribution;
    selectedSlotIso?: string;
    salesNote?: string;
  };
}

export interface CheckOutResult {
  bookingId: string;
  pieceId?: string;
  pieceCompleted?: boolean;
  rolledDepositId?: string;
  /** Populated when Outcome A creates the next session inline. */
  newBookingId?: string;
}

export function checkOutBooking(input: CheckOutInput): CheckOutResult {
  const brain = useBrainStore.getState();
  const booking = brain.bookings[input.bookingId];
  if (!booking) return { bookingId: input.bookingId };
  const at = nowIso();

  let rolledDepositId: string | undefined;
  if (input.rolloverDepositToNextBooking && booking.depositId) {
    const original = brain.deposits[booking.depositId];
    if (original) {
      // The money that actually rolls = what was paid on the original.
      const paidSoFar = depositPaidAud(original);
      const nextId = seedId('dep-roll') ?? `dep-roll-${booking.id}-${Date.now()}`;
      const next: Deposit = {
        id: nextId,
        customerId: original.customerId,
        studioId: original.studioId,
        artistId: original.artistId ?? booking.artistId,
        amountAud: original.amountAud,
        feesAud: 0,
        netAud: original.amountAud,
        status: 'paid',
        channelSent: original.channelSent,
        stripePaymentLinkId: `plink_roll_${booking.id.slice(0, 8)}`,
        expiresAt: new Date(new Date(at).getTime() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        createdAt: at,
        viewedAt: at,
        paidAt: at,
        policy: original.policy,
        source: 'in-chair-rebooking',
        // Movement (G2): the money arrived here, linked back to the original.
        ledger: [
          {
            at,
            amountAud: paidSoFar,
            kind: 'rollover-in',
            counterpartDepositId: original.id,
            fromBookingId: booking.id,
            by: 'system',
            note: 'Rolled from previous session',
          },
        ],
      };
      // Preserve the original's paid history (backfill a single paid entry when it
      // has none) so its ledger nets to zero after the rollover-out.
      const originalLedger =
        original.ledger && original.ledger.length > 0
          ? original.ledger
          : [
              {
                at: original.paidAt ?? at,
                amountAud: paidSoFar,
                kind: 'paid' as const,
                by: original.takenByAgentId ?? 'system',
              },
            ];
      brain.upsertDeposit(next);
      brain.upsertDeposit({
        ...original,
        status: 'rolled-over',
        ledger: [
          ...originalLedger,
          {
            at,
            amountAud: paidSoFar,
            kind: 'rollover-out',
            counterpartDepositId: nextId,
            by: 'system',
            note: 'Rolled to next session',
          },
        ],
      });
      rolledDepositId = nextId;
    }
  }

  brain.upsertBooking({
    ...booking,
    status: 'completed',
    checkedOutAt: at,
    sessionNotes: input.sessionNote,
    amountPaidAud: input.amountPaidAud,
    paymentMethod: input.paymentMethod,
    paidAt: at,
    rolledDepositId,
  });

  // Record the make-sale (E1): the system money record E3 reconciles and E2's
  // cash ledger consumes. Merch is a separate no-commission line.
  const merchLines = input.merchLines ?? [];
  const merchAud = merchLines.reduce((sum, l) => sum + l.priceAud, 0);
  const adjustmentAud = input.adjustmentAud ?? 0;
  const collectNowAud = input.amountPaidAud + merchAud + adjustmentAud;
  // Net cash to the till = tendered − change = the collect-now total when paid in
  // cash. Card settles to the bank (no till cash); mixed uses its cash portion.
  const cashInAud =
    input.paymentMethod === 'cash'
      ? collectNowAud
      : input.paymentMethod === 'mixed'
        ? Math.min(input.cashTenderedAud ?? 0, collectNowAud)
        : 0;
  const cust = brain.customers[booking.customerId];
  const artistRec = brain.artists[booking.artistId];
  const sale: SalePayment = {
    id: `sale-${booking.id}`,
    bookingId: booking.id,
    studioId: booking.studioId,
    artistId: booking.artistId,
    customerName: cust ? `${cust.firstName} ${cust.lastName}` : booking.customerId,
    artistName: artistRec?.displayName ?? booking.artistId,
    method: input.paymentMethod,
    balanceAud: input.amountPaidAud,
    depositAppliedAud: input.depositAppliedAud ?? 0,
    merchAud,
    merchLines,
    adjustmentAud,
    adjustmentReason: input.adjustmentReason,
    cashInAud,
    saleReference: makeSaleReference(booking.studioId, booking.id, at),
    takenAt: at,
    afterCutoff: hourOfIso(at) >= SETTLEMENT_CUTOFF_HOUR,
  };
  brain.upsertSalePayment(sale);

  // Post cash into the day's ledger; the opening float is untouched by a sale.
  if (cashInAud > 0) {
    const ledger = cashLedgerFor(booking.studioId, at);
    brain.upsertCashLedger({ ...ledger, cashInAud: ledger.cashInAud + cashInAud });
  }

  const pieceId: string | undefined = booking.pieceId;
  let pieceCompleted = false;
  if (booking.pieceId) {
    const piece = brain.pieces[booking.pieceId];
    if (piece) {
      const added = input.hoursToAdd ?? booking.durationMin / 60;
      const sessionsCompleted = (piece.sessionsCompleted ?? piece.bookingsLinked.length) + 1;
      // L3: a partway decline updates the living estimate to completed-so-far +
      // remaining. Never overwrite sessionsCompleted, and never below it.
      const estimatedTotalSessions =
        !input.markPieceComplete && input.pieceSessionsLeft != null
          ? sessionsCompleted + Math.max(0, input.pieceSessionsLeft)
          : piece.estimatedTotalSessions;
      const updated: Piece = {
        ...piece,
        hoursCompleted: Math.min(piece.estimatedTotalHours, piece.hoursCompleted + added),
        sessionsCompleted,
        estimatedTotalSessions,
        status: input.markPieceComplete ? 'completed' : piece.status,
        lastSessionDate: at,
      };
      brain.upsertPiece(updated);
      pieceCompleted = input.markPieceComplete === true;
    }
  }

  // tv-b5g / Diagram 02: a deposit is "used up when the piece completes". When
  // this checkout completes the piece and the booking carries a deposit, write an
  // explicit `redeemed` movement so redemption is a record, not only a reporting
  // derivation. (A rolled-over deposit has already moved out, so skip it.)
  if (pieceCompleted && booking.depositId) {
    const redeemedDeposit = brain.deposits[booking.depositId];
    if (redeemedDeposit && redeemedDeposit.status !== 'rolled-over') {
      const redeemedAmount = depositPaidAud(redeemedDeposit);
      brain.upsertDeposit({
        ...redeemedDeposit,
        ledger: [
          ...(redeemedDeposit.ledger ?? []),
          {
            at,
            amountAud: redeemedAmount,
            kind: 'redeemed' as const,
            by: 'system',
            fromBookingId: booking.id,
            ...(pieceId ? { toPieceId: pieceId } : {}),
            note: 'Deposit redeemed on piece completion',
          },
        ],
      });
    }
  }

  if (input.sessionNote) {
    brain.addRapportNote(
      booking.customerId,
      input.sessionNote,
      `In-chair · ${booking.artistId}`,
      at,
    );
  }

  if (input.futureReadinessPhrase) {
    const signalId =
      seedId('frs-checkout') ??
      `frs-checkout-${booking.id}-${Math.floor(Math.random() * 1e6).toString(36)}`;
    const signal: FutureReadinessSignal = {
      id: signalId,
      customerId: booking.customerId,
      rawPhrase: input.futureReadinessPhrase,
      scheduledFor:
        input.futureReadinessTriggerDate ??
        new Date(new Date(at).getTime() + 28 * 24 * 60 * 60 * 1000).toISOString(),
      recommendedChannel: 'sms',
      status: 'scheduled',
      createdAt: at,
    };
    brain.addFutureReadinessSignal(signal);
  }

  brain.appendActivity({
    id: `act-checkout-${booking.id}`,
    type: 'booking-completed',
    customerId: booking.customerId,
    studioId: booking.studioId,
    artistId: booking.artistId,
    amount: input.amountPaidAud,
    body: `${booking.customerId} checked out · paid $${input.amountPaidAud}${rolledDepositId ? ` · deposit rolled forward` : ''}.`,
    timestamp: at,
  });

  // Process the in-chair rebook outcome (if any). The brain store keeps the
  // outcome + attribution on the source booking; for Outcome A we also create
  // the next Booking record so the leaderboard / calendar pick it up; for
  // Outcome C we seed the sales follow-up task that lands in the queue.
  let newBookingId: string | undefined;
  if (input.rebookCapture) {
    const { outcome, attribution, selectedSlotIso, salesNote } = input.rebookCapture;
    brain.setRebookOutcome(booking.id, outcome, attribution);

    if (outcome.kind === 'rebookedInChair' && selectedSlotIso) {
      const newBooking: Booking = {
        id: outcome.newBookingId,
        customerId: booking.customerId,
        studioId: booking.studioId,
        artistId: booking.artistId,
        pieceId: booking.pieceId,
        start: selectedSlotIso,
        durationMin: booking.durationMin,
        sessionLength: booking.sessionLength,
        status: 'confirmed',
        depositId: rolledDepositId,
        designBrief: booking.designBrief,
        referencePhotos: booking.referencePhotos,
        source: 'in-chair-rebooking',
        createdAt: at,
        sourceBookingId: booking.id,
        inChairRebooking: true,
      };
      brain.upsertBooking(newBooking);
      newBookingId = newBooking.id;
      // Next session is born `confirmed` — auto-send its waiver.
      onBookingConfirmed(newBooking.id);
      brain.appendActivity({
        id: `act-rebook-attr-${booking.id}`,
        type: 'rebook-attributed',
        customerId: booking.customerId,
        studioId: booking.studioId,
        artistId: booking.artistId,
        body: `Rebooked in chair · attributed to ${attribution.facilitatedBy} (${attribution.facilitatedByStaffId}).`,
        timestamp: at,
      });
    } else if (outcome.kind === 'willRebookLater') {
      // L3 (17 Jul): "will rebook later" IS the deferral now. Deposit rolls
      // forward as a held pot (rollover block above), no booking created, AND we
      // seed the same sales follow-up task that "defer to sales" used to, carrying
      // the mandatory reason as its note.
      brain.upsertRebookFollowup({
        id: `rfu-${booking.id}`,
        customerId: booking.customerId,
        studioId: booking.studioId,
        sourceBookingId: booking.id,
        originalArtistId: booking.artistId,
        note: outcome.note,
        createdAt: at,
      });
      brain.appendActivity({
        id: `act-rebook-later-${booking.id}`,
        type: 'rebook-attributed',
        customerId: booking.customerId,
        studioId: booking.studioId,
        artistId: booking.artistId,
        body: `Will rebook later · deposit rolled forward to hold${outcome.note ? ` · "${outcome.note}"` : ''} · sales to follow up.`,
        timestamp: at,
      });
    } else if (outcome.kind === 'noRebookDepositApplied') {
      brain.appendActivity({
        id: `act-rebook-decl-${booking.id}`,
        type: 'rebook-declined',
        customerId: booking.customerId,
        studioId: booking.studioId,
        artistId: booking.artistId,
        body: `Declined a rebook · reason: ${outcome.reason}${outcome.reasonNote ? ` (${outcome.reasonNote})` : ''}.`,
        timestamp: at,
      });
      // No rebook AND still mid-piece (a rebook needs no reason - the deposit just
      // rolls - but not rebooking mid-piece is the leak): raise the Ops flag with
      // the mandatory reason (tv-3gl leg a). Leg b (the Sweeper open-business
      // landing) is emergent from leaving the piece in-progress.
      if (booking.pieceId && !input.markPieceComplete) {
        flagNoRebookMidPiece({
          bookingId: booking.id,
          reason: String(outcome.reason),
          reasonNote: outcome.reasonNote,
        });
      }
    } else if (outcome.kind === 'rebookDeferredToSales') {
      brain.upsertRebookFollowup({
        id: outcome.salesTaskId,
        customerId: booking.customerId,
        studioId: booking.studioId,
        sourceBookingId: booking.id,
        originalArtistId: booking.artistId,
        note: salesNote,
        createdAt: at,
      });
      brain.appendActivity({
        id: `act-rebook-def-${booking.id}`,
        type: 'rebook-deferred-to-sales',
        customerId: booking.customerId,
        studioId: booking.studioId,
        artistId: booking.artistId,
        body: `Deferred rebook to sales team${salesNote ? ` · "${salesNote}"` : ''}.`,
        timestamp: at,
      });
    }
  }

  // Healing-check (09 Jul): a completed session that did NOT rebook in the chair
  // earns a T+7d "how's it healing?" nudge. Minted once per booking (stable id);
  // money cohort when a paid deposit still sits on the piece.
  if (input.rebookCapture?.outcome.kind !== 'rebookedInChair') {
    const pieceDeposit = booking.pieceId
      ? Object.values(brain.deposits).find(
          (d) => d.pieceId === booking.pieceId && d.status === 'paid' && depositPaidAud(d) > 0,
        )
      : undefined;
    brain.upsertHealingCheck({
      id: `heal-${booking.id}`,
      customerId: booking.customerId,
      studioId: booking.studioId,
      sourceBookingId: booking.id,
      pieceId: booking.pieceId,
      depositId: pieceDeposit?.id,
      createdAt: at,
      dueAt: new Date(new Date(at).getTime() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    });
  }

  brain.incStat('inChairRebookings', input.rolloverDepositToNextBooking ? 1 : 0);
  playSound('deposit-chime');
  eventBus.publish('booking:checked-out', {
    bookingId: booking.id,
    rolledDepositId,
  });
  return { bookingId: booking.id, pieceId, pieceCompleted, rolledDepositId, newBookingId };
}

/**
 * The artist wrapping their session with a note. Zack: "the manager deals with
 * all the rebooks" — so the artist doesn't record an outcome or take payment;
 * they just leave a rapport note (what they learned in the chair) that lands on
 * the customer record for the next conversation.
 *
 * Deliberately narrow: it never touches status, payment, or the rebook. A blank
 * note is a no-op. The manager owns checkout + the rebook read at the make-sale.
 */
export function recordArtistSessionNote(
  bookingId: string,
  note: string,
): Booking | undefined {
  const brain = useBrainStore.getState();
  const booking = brain.bookings[bookingId];
  if (!booking) return undefined;

  const trimmed = note.trim();
  if (!trimmed) return booking;

  const at = nowIso();
  brain.addRapportNote(booking.customerId, trimmed, `In-chair · ${booking.artistId}`, at);
  brain.appendActivity({
    id: `act-artist-note-${booking.id}`,
    type: 'booking-session-noted',
    customerId: booking.customerId,
    studioId: booking.studioId,
    artistId: booking.artistId,
    body: `${booking.artistId} wrapped ${booking.customerId}'s session and left a note.`,
    timestamp: at,
  });
  eventBus.publish('booking:artist-session-note', { bookingId });
  return booking;
}

/** A photo the artist captured in the chair, tagged with what it is. */
export interface CapturedPhoto {
  url: string;
  kind: SessionPhotoKind;
}

/**
 * Photos the artist captured in the chair. They hang off the *piece* -
 * `Piece.progressPhotos` carries a `bookingId`, so each photo still knows which
 * sitting it came from and surfaces under that piece on the client's history.
 *
 * Each carries its `kind`: the work itself, or the reference it was worked from.
 * The caption names both the sitting and the kind, so a photo still explains
 * itself in a lightbox months later.
 *
 * Kept separate from `recordArtistSessionNote` on purpose: that function treats a
 * blank note as a no-op, so folding photos into it would silently drop them
 * whenever the artist adds pictures without writing anything.
 *
 * A booking with no piece has nothing to attach to - the caller disables capture
 * in that case, and this is a no-op as a backstop.
 */
export function recordArtistProgressPhotos(
  bookingId: string,
  photos: CapturedPhoto[],
  sessionNo?: number | null,
): Booking | undefined {
  const brain = useBrainStore.getState();
  const booking = brain.bookings[bookingId];
  if (!booking) return undefined;
  if (photos.length === 0 || !booking.pieceId) return booking;

  const at = nowIso();
  for (const photo of photos) {
    brain.addPieceProgressPhoto(booking.pieceId, {
      url: photo.url,
      capturedAt: at,
      bookingId,
      kind: photo.kind,
      caption: sessionNo != null ? `Session ${sessionNo} ${photo.kind}` : photo.kind,
    });
  }
  const refs = photos.filter((p) => p.kind === 'reference').length;
  brain.appendActivity({
    id: `act-artist-photos-${booking.id}`,
    type: 'booking-session-noted',
    customerId: booking.customerId,
    studioId: booking.studioId,
    artistId: booking.artistId,
    body: `${booking.artistId} added ${photos.length} photo${photos.length === 1 ? '' : 's'}${refs > 0 ? ` (${refs} reference)` : ''} to ${booking.customerId}'s piece.`,
    timestamp: at,
  });
  eventBus.publish('booking:artist-session-note', { bookingId });
  return booking;
}

/**
 * Maps a rebook read (will the customer come back) to the outcome the manager's
 * panel should open on. It's a starting point, not a decision: "will rebook"
 * opens on the in-chair rebook, "no rebook" opens on deposit-applied. No read →
 * no pre-selection. The manager is always free to override.
 */
export function prefillOutcomeFromIntent(
  intent: ArtistRebookIntent | undefined,
): RebookOutcome['kind'] | null {
  if (!intent) return null;
  return intent.willRebook ? 'rebookedInChair' : 'noRebookDepositApplied';
}

export function elapsedSeconds(checkedInIso: string, nowMs: number): number {
  return Math.max(0, Math.floor((nowMs - new Date(checkedInIso).getTime()) / 1000));
}
